import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CalendarDays, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/api";
import { formatDate } from "@/constants";
import DateTimeField from "@/components/DateTimeField";

const EMPTY_EVENT = { title: "", description: "", date: "", end_date: "", location: "", members_only: false, published: true, image_url: "", registration_closed: false, closes_at: "", max_participations: 0, internal_code: "", required_color: "" };
const EMPTY_NEWS = { title: "", content: "", published: true };

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const SEASONAL_OPTIONS = [
  { value: "none", label: "Aucun effet", desc: "site classique" },
  { value: "halloween", label: "Halloween", desc: "citrouilles, confettis orange/violet + bandeau animé" },
  { value: "noel", label: "Noël", desc: "mini-clip féérique (village, traîneau, Père Noël) + neige sur le site" },
  { value: "epiphanie", label: "Épiphanie", desc: "mini-clip galette des rois + confettis dorés" },
  { value: "chandeleur", label: "Chandeleur", desc: "mini-clip crêpe lancée qui colle au plafond" },
];

export default function AdminContent() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [eventDialog, setEventDialog] = useState(false);
  const [newsDialog, setNewsDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingNews, setEditingNews] = useState(null);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [eventFile, setEventFile] = useState(null);
  const [newsForm, setNewsForm] = useState(EMPTY_NEWS);
  const [saving, setSaving] = useState(false);
  const [cetf, setCetf] = useState(null);
  const [cetfSaving, setCetfSaving] = useState(false);
  const [participants, setParticipants] = useState([]);
  const loadParticipants = () => api.get("/admin/event-participations").then((r) => setParticipants(r.data)).catch((e) => toast.error(formatApiError(e)));

  const [ideas, setIdeas] = useState([]);
  const loadIdeas = () => api.get("/admin/ideas").then((r) => setIdeas(r.data)).catch((e) => toast.error(formatApiError(e)));
  const deleteIdea = async (id) => {
    try {
      await api.delete(`/admin/ideas/${id}`);
      toast.success("Idée supprimée");
      loadIdeas();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const setIdeaStatus = async (id, status) => {
    try {
      await api.post(`/admin/ideas/${id}/status`, { status });
      toast.success("Statut mis à jour");
      loadIdeas();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const participantsByEvent = {};
  participants.forEach((p) => { (participantsByEvent[p.event_title] = participantsByEvent[p.event_title] || []).push(p); });

  const toggleRegistration = async (ev) => {
    try {
      await api.post(`/admin/events/${ev.id}/toggle-registration`, { closed: !ev.registration_closed });
      toast.success(!ev.registration_closed ? `Inscriptions clôturées pour « ${ev.title} »` : `Inscriptions rouvertes pour « ${ev.title} »`);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAudience = async (ev) => {
    try {
      await api.post(`/admin/events/${ev.id}/toggle-audience`, { members_only: !ev.members_only });
      toast.success(!ev.members_only
        ? `« ${ev.title} » est désormais réservé aux adhérents (code + couleur exigés)`
        : `« ${ev.title} » est désormais ouvert au public`);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadCetf = () => api.get("/admin/liste-cetf/settings").then((r) => setCetf(r.data)).catch((e) => toast.error(formatApiError(e)));

  const saveCetf = async (overrides = {}) => {
    setCetfSaving(true);
    try {
      const r = await api.post("/admin/liste-cetf/settings", { ...cetf, ...overrides });
      setCetf(r.data);
      toast.success("Page Liste CET mise à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCetfSaving(false);
    }
  };

  const [retro, setRetro] = useState([]);
  const [retroForm, setRetroForm] = useState({ title: "", link: "", file: null });
  const [retroSaving, setRetroSaving] = useState(false);
  const [music, setMusic] = useState(null);
  const [musicForm, setMusicForm] = useState({ title: "", file: null });
  const [musicSaving, setMusicSaving] = useState(false);
  const [cetMedia, setCetMedia] = useState([]);
  const [cetForm, setCetForm] = useState({ title: "", file: null });
  const [cetMediaSaving, setCetMediaSaving] = useState(false);

  const loadRetro = () => api.get("/admin/retro").then((r) => setRetro(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadMusic = () => api.get("/admin/music").then((r) => setMusic(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadCetMedia = () => api.get("/admin/cetf-media").then((r) => setCetMedia(r.data)).catch((e) => toast.error(formatApiError(e)));

  const sendMedia = async (endpoint, form, setSaving, reload, reset, label) => {
    if (!form.file) { toast.error("Choisissez un fichier à déposer"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      if (form.title) fd.append("title", form.title);
      if (form.link) fd.append("link", form.link);
      fd.append("file", form.file);
      await api.post(endpoint, fd);
      toast.success(label);
      reset();
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteMedia = async (endpoint, reload) => {
    try {
      await api.delete(endpoint);
      toast.success("Élément supprimé");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleMusicEnabled = async () => {
    try {
      const r = await api.post("/admin/music-settings", { enabled: !music?.enabled });
      setMusic((m) => ({ ...(m || {}), enabled: r.data.enabled }));
      toast.success(r.data.enabled ? "Musique activée sur le site" : "Musique désactivée sur le site");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePublished = async (endpoint, reload) => {
    try {
      await api.post(endpoint);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const mediaUrl = (prefix, p) => `${process.env.REACT_APP_BACKEND_URL}/api/${prefix}/${p}`;

  const [navAds, setNavAds] = useState([]);
  const [navAdForm, setNavAdForm] = useState({ title: "", link: "", file: null });
  const [navAdSaving, setNavAdSaving] = useState(false);
  const loadNavAds = () => api.get("/admin/nav-ads").then((r) => setNavAds(r.data)).catch((e) => toast.error(formatApiError(e)));

  const [legalDocs, setLegalDocs] = useState({});
  const [legalSaving, setLegalSaving] = useState("");
  const loadLegalDocs = () => api.get("/admin/legal-docs").then((r) => setLegalDocs(r.data || {})).catch((e) => toast.error(formatApiError(e)));

  const uploadLegalDoc = async (kind, file) => {
    if (!file) return;
    setLegalSaving(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/legal-docs/${kind}`, fd);
      toast.success("Document mis en ligne");
      loadLegalDocs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLegalSaving("");
    }
  };

  const removeLegalDoc = async (kind) => {
    try {
      await api.delete(`/admin/legal-docs/${kind}`);
      toast.success("Document supprimé");
      loadLegalDocs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [seasonal, setSeasonal] = useState(null);
  const [seasonalSaving, setSeasonalSaving] = useState(false);

  const loadSeasonal = () => api.get("/seasonal").then((r) => setSeasonal(r.data)).catch(() => {});

  const saveSeasonal = async (overrides = {}) => {
    setSeasonalSaving(true);
    try {
      const r = await api.post("/admin/seasonal", { mode: "none", seasonal_text: "", ...seasonal, ...overrides });
      setSeasonal(r.data);
      toast.success("Effets saisonniers mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSeasonalSaving(false);
    }
  };

  const loadEvents = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadNews = () => api.get("/admin/news").then((r) => setNews(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { loadEvents(); loadNews(); loadCetf(); loadRetro(); loadMusic(); loadCetMedia(); loadSeasonal(); loadNavAds(); loadLegalDocs(); loadParticipants(); loadIdeas(); }, []);

  const openEventCreate = () => { setEditingEvent(null); setEventForm(EMPTY_EVENT); setEventFile(null); setEventDialog(true); };
  const openEventEdit = (ev) => {
    setEditingEvent(ev);
    setEventForm({ ...EMPTY_EVENT, ...ev, date: toDatetimeLocal(ev.date), end_date: toDatetimeLocal(ev.end_date), closes_at: toDatetimeLocal(ev.closes_at) });
    setEventFile(null);
    setEventDialog(true);
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!eventForm.date) { toast.error("Choisissez une date et une heure pour l'événement"); return; }
      let image_url = eventForm.image_url || "";
      if (eventFile) {
        const fd = new FormData();
        fd.append("file", eventFile);
        const up = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_url = `/api/event-images/${up.data.storage_path}`;
      }
      const payload = { ...eventForm, image_url, date: new Date(eventForm.date).toISOString(),
        end_date: eventForm.end_date ? new Date(eventForm.end_date).toISOString() : "",
        closes_at: eventForm.closes_at ? new Date(eventForm.closes_at).toISOString() : "" };
      if (editingEvent) {
        await api.patch(`/admin/events/${editingEvent.id}`, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Événement publié");
      }
      setEventDialog(false);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Supprimer l'événement « ${ev.title} » ?`)) return;
    try {
      await api.delete(`/admin/events/${ev.id}`);
      toast.success("Événement supprimé");
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openNewsCreate = () => { setEditingNews(null); setNewsForm(EMPTY_NEWS); setNewsDialog(true); };
  const openNewsEdit = (n) => { setEditingNews(n); setNewsForm({ title: n.title, content: n.content, published: n.published }); setNewsDialog(true); };

  const submitNews = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingNews) {
        await api.patch(`/admin/news/${editingNews.id}`, newsForm);
        toast.success("Actualité mise à jour");
      } else {
        await api.post("/admin/news", newsForm);
        toast.success("Actualité publiée");
      }
      setNewsDialog(false);
      loadNews();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteNews = async (n) => {
    if (!window.confirm(`Supprimer l'actualité « ${n.title} » ?`)) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("Actualité supprimée");
      loadNews();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pubBadge = (published) => (
    <Badge className={published
      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
      : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}>
      {published ? "Publié" : "Brouillon"}
    </Badge>
  );

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-content-title">Contenus</h1>
        <p className="text-sm text-slate-500 mt-1">Publiez les événements et actualités visibles sur le site.</p>
      </div>

      <Tabs defaultValue="events">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="events" data-testid="admin-tab-events">Événements ({events.length})</TabsTrigger>
          <TabsTrigger value="news" data-testid="admin-tab-news">Actualités ({news.length})</TabsTrigger>
          <TabsTrigger value="cetf" data-testid="admin-tab-cetf">Liste CET</TabsTrigger>
          <TabsTrigger value="retro" data-testid="admin-tab-retro">Second bandeau publicitaire ({retro.length})</TabsTrigger>
          <TabsTrigger value="music" data-testid="admin-tab-music">Musique</TabsTrigger>
          <TabsTrigger value="season" data-testid="admin-tab-season">Saison</TabsTrigger>
          <TabsTrigger value="legal" data-testid="admin-tab-legal">Documents contractuels</TabsTrigger>
          <TabsTrigger value="participants" data-testid="admin-tab-participants">Participants</TabsTrigger>
          <TabsTrigger value="idees" data-testid="admin-tab-idees">Boîte à idées</TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-sky-700 hover:bg-sky-800" onClick={openEventCreate} data-testid="event-create-btn">
              <Plus className="h-4 w-4 mr-2" />Nouvel événement
            </Button>
          </div>
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="events-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lieu</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Inscriptions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-8" data-testid="events-admin-empty">
                      Aucun événement. Créez le premier !
                    </TableCell></TableRow>
                  )}
                  {events.map((ev) => (
                    <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                      <TableCell className="font-medium">{ev.title}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{formatDate(ev.date)}</span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {ev.location ? <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{ev.location}</span> : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge className={ev.members_only
                          ? "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"
                          : "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100"}>
                          {ev.members_only ? "Adhérents" : "Public"}
                        </Badge>
                      </TableCell>
                      <TableCell>{pubBadge(ev.published)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1.5">
                          <Button variant="outline" size="sm"
                            className={ev.registration_closed
                              ? "text-red-700 border-red-300 hover:bg-red-50"
                              : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}
                            onClick={() => toggleRegistration(ev)} data-testid={`event-registration-toggle-${ev.id}`}>
                            {ev.registration_closed ? "Clôturé — rouvrir" : "Ouvert — clôturer"}
                          </Button>
                          <Button variant="outline" size="sm"
                            className={ev.members_only
                              ? "text-amber-700 border-amber-300 hover:bg-amber-50"
                              : "text-sky-700 border-sky-300 hover:bg-sky-50"}
                            onClick={() => toggleAudience(ev)} data-testid={`event-audience-toggle-${ev.id}`}>
                            {ev.members_only ? "Ouvert aux adhérents" : "Ouvert au public"}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => openEventEdit(ev)} data-testid={`event-edit-${ev.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deleteEvent(ev)} data-testid={`event-delete-${ev.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="news" className="space-y-4">
          <div className="flex justify-end">
            <Button className="bg-sky-700 hover:bg-sky-800" onClick={openNewsCreate} data-testid="news-create-btn">
              <Plus className="h-4 w-4 mr-2" />Nouvelle actualité
            </Button>
          </div>
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="news-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Publié le</TableHead>
                    <TableHead>Auteur</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {news.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8" data-testid="news-admin-empty">
                      Aucune actualité. Publiez la première !
                    </TableCell></TableRow>
                  )}
                  {news.map((n) => (
                    <TableRow key={n.id} data-testid={`news-row-${n.id}`}>
                      <TableCell className="font-medium max-w-[280px] truncate">{n.title}</TableCell>
                      <TableCell className="text-sm">{formatDate(n.created_at)}</TableCell>
                      <TableCell className="text-sm">{n.author || "—"}</TableCell>
                      <TableCell>{pubBadge(n.published)}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => openNewsEdit(n)} data-testid={`news-edit-${n.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deleteNews(n)} data-testid={`news-delete-${n.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cetf" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-5" data-testid="cetf-content-card">
            {!cetf ? (
              <p className="text-sm text-slate-500" data-testid="cetf-loading">Chargement…</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Page Liste CET</h2>
                    <p className="text-sm text-slate-500">Titre, couleur, police, textes et dépôt de candidature de la page publique.</p>
                  </div>
                  <Badge className={cetf.candidacy_open
                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                    : "bg-red-100 text-red-800 border border-red-200 hover:bg-red-100"}
                    data-testid="cetf-candidacy-badge">
                    {cetf.candidacy_open ? "Dépôt de candidature activé" : "Dépôt de candidature clôturé"}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cetf-title">Titre de la liste</Label>
                    <Input id="cetf-title" value={cetf.list_title} maxLength={60}
                      onChange={(e) => setCetf({ ...cetf, list_title: e.target.value })} data-testid="cetf-title-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cetf-academy">Cartouche académie (à côté de la Marianne)</Label>
                    <Input id="cetf-academy" value={cetf.academy_label} maxLength={80}
                      onChange={(e) => setCetf({ ...cetf, academy_label: e.target.value })} data-testid="cetf-academy-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cetf-subtitle">Sous-titre</Label>
                  <Input id="cetf-subtitle" value={cetf.list_subtitle} maxLength={200}
                    onChange={(e) => setCetf({ ...cetf, list_subtitle: e.target.value })} data-testid="cetf-subtitle-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cetf-color">Couleur du titre</Label>
                    <div className="flex items-center gap-3">
                      <input id="cetf-color" type="color" value={cetf.title_color}
                        onChange={(e) => setCetf({ ...cetf, title_color: e.target.value })}
                        className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer bg-transparent" data-testid="cetf-color-input" />
                      <span className="text-sm text-slate-500 font-mono" data-testid="cetf-color-value">{cetf.title_color}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Police du titre</Label>
                    <Select value={cetf.title_font} onValueChange={(v) => setCetf({ ...cetf, title_font: v })}>
                      <SelectTrigger data-testid="cetf-font-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Plus Jakarta Sans", "Outfit", "Montserrat", "Playfair Display", "Georgia"].map((f) => (
                          <SelectItem key={f} value={f} data-testid={`cetf-font-${f.replace(/\s+/g, "-").toLowerCase()}`}>
                            <span style={{ fontFamily: `'${f}', sans-serif` }}>{f}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cetf-closed-title">Titre affiché quand le dépôt est clôturé</Label>
                    <Input id="cetf-closed-title" value={cetf.closed_title} maxLength={120}
                      onChange={(e) => setCetf({ ...cetf, closed_title: e.target.value })} data-testid="cetf-closed-title-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cetf-year">Prochaine année scolaire affichée</Label>
                    <Input id="cetf-year" value={cetf.next_school_year} maxLength={20}
                      onChange={(e) => setCetf({ ...cetf, next_school_year: e.target.value })} data-testid="cetf-year-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cetf-closed-message">Message affiché quand le dépôt est clôturé</Label>
                  <Textarea id="cetf-closed-message" rows={2} maxLength={400} value={cetf.closed_message}
                    onChange={(e) => setCetf({ ...cetf, closed_message: e.target.value })} data-testid="cetf-closed-message-input" />
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200">
                  <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => saveCetf()} disabled={cetfSaving} data-testid="cetf-save-btn">
                    {cetfSaving ? "Enregistrement…" : "Enregistrer les contenus"}
                  </Button>
                  <Button
                    className={cetf.candidacy_open ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
                    onClick={() => saveCetf({ candidacy_open: !cetf.candidacy_open })}
                    disabled={cetfSaving}
                    data-testid="cetf-toggle-candidacy-btn">
                    {cetf.candidacy_open ? "Clôturer le dépôt de candidature" : "Activer le dépôt de candidature"}
                  </Button>
                </div>
              </>
            )}
          </Card>

          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="cetf-media-admin-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Médiathèque de la page Liste CET</h2>
              <p className="text-sm text-slate-500">Images (PNG, JPEG…), documents PDF et vidéos/animations affichés publiquement sur la page Liste CET (30 Mo max par fichier).</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cetm-title">Titre / légende</Label>
                <Input id="cetm-title" value={cetForm.title} maxLength={120}
                  onChange={(e) => setCetForm({ ...cetForm, title: e.target.value })} data-testid="cetf-media-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cetm-file">Fichier (image, PDF ou vidéo)</Label>
                <Input id="cetm-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4,video/webm,video/quicktime"
                  onChange={(e) => setCetForm({ ...cetForm, file: e.target.files?.[0] || null })} data-testid="cetf-media-file-input" />
              </div>
            </div>
            <Button className="bg-sky-700 hover:bg-sky-800" disabled={cetMediaSaving}
              onClick={() => sendMedia("/admin/cetf-media", cetForm, setCetMediaSaving, loadCetMedia, () => setCetForm({ title: "", file: null }), "Fichier publié sur la page Liste CET")}
              data-testid="cetf-media-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {cetMediaSaving ? "Envoi en cours…" : "Publier le fichier"}
            </Button>
            {cetMedia.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                {cetMedia.map((m) => (
                  <div key={m.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`cetf-media-admin-${m.id}`}>
                    {m.media_type === "image" ? (
                      <img src={mediaUrl("cetf-media-files", m.storage_path)} alt={m.title} className="w-full h-24 object-cover" />
                    ) : m.media_type === "video" ? (
                      <video src={mediaUrl("cetf-media-files", m.storage_path)} className="w-full h-24 object-cover" muted preload="metadata" />
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center bg-slate-100 text-[10px] font-bold uppercase tracking-widest text-slate-500">PDF</div>
                    )}
                    <div className="p-2 space-y-1.5">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs text-slate-700 truncate">{m.title || "Sans titre"}</p>
                        <Badge className={m.published
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 shrink-0 text-[10px] px-1.5 py-0"
                          : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 shrink-0 text-[10px] px-1.5 py-0"}
                          data-testid={`cetf-media-status-${m.id}`}>
                          {m.published ? "Publié" : "Brouillon"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm"
                          className={m.published ? "bg-slate-500 hover:bg-slate-600 flex-1 h-7 text-xs" : "bg-emerald-600 hover:bg-emerald-700 flex-1 h-7 text-xs"}
                          onClick={() => togglePublished(`/admin/cetf-media/${m.id}/toggle`, loadCetMedia)} data-testid={`cetf-media-toggle-${m.id}`}>
                          {m.published ? "Désactiver" : "Publier"}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                          onClick={() => deleteMedia(`/admin/cetf-media/${m.id}`, loadCetMedia)} data-testid={`cetf-media-delete-${m.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="cetf-nav-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Bandeau « Liste CET » dans le menu du site</h2>
                <p className="text-sm text-slate-500">Hors période électorale, masquez l'entrée Liste CET du menu et de la page d'accueil : le bandeau partenaires « Publicité · Nos amis » (logos, images publicitaires, petits films) prend alors leur place.</p>
              </div>
              <div className="flex items-center gap-2.5">
                <Label htmlFor="cetf-nav-visible" className="text-sm text-slate-700">Afficher « Liste CET » dans le menu</Label>
                <Switch id="cetf-nav-visible" checked={cetf?.nav_visible !== false} disabled={!cetf}
                  onCheckedChange={(v) => cetf && saveCetf({ nav_visible: v })} data-testid="cetf-nav-visible-switch" />
              </div>
            </div>
          </Card>

          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="nav-ads-admin-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Bandeau partenaires (hors période électorale)</h2>
              <p className="text-sm text-slate-500">Logos d'amis de l'association, images publicitaires ou mini-clips — affichés en haut du site quand « Liste CET » est masqué du menu (défilement toutes les 4 secondes, liens cliquables).</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="navad-title">Titre / nom du partenaire</Label>
                <Input id="navad-title" value={navAdForm.title} maxLength={120}
                  onChange={(e) => setNavAdForm({ ...navAdForm, title: e.target.value })} data-testid="navad-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="navad-link">Lien (optionnel)</Label>
                <Input id="navad-link" value={navAdForm.link} maxLength={300} placeholder="https://…"
                  onChange={(e) => setNavAdForm({ ...navAdForm, link: e.target.value })} data-testid="navad-link-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="navad-file">Fichier (logo, image ou vidéo, 30 Mo max)</Label>
                <Input id="navad-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  onChange={(e) => setNavAdForm({ ...navAdForm, file: e.target.files?.[0] || null })} data-testid="navad-file-input" />
              </div>
            </div>
            <Button className="bg-sky-700 hover:bg-sky-800" disabled={navAdSaving}
              onClick={() => sendMedia("/admin/nav-ads", navAdForm, setNavAdSaving, loadNavAds, () => setNavAdForm({ title: "", link: "", file: null }), "Partenaire ajouté (brouillon)")}
              data-testid="navad-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {navAdSaving ? "Envoi en cours…" : "Ajouter le partenaire"}
            </Button>
            {navAds.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {navAds.map((a) => (
                  <div key={a.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`navad-item-${a.id}`}>
                    <div className="h-20 flex items-center justify-center bg-slate-50">
                      {a.media_type === "video" ? (
                        <video src={mediaUrl("nav-ads-media", a.storage_path)} className="h-full w-auto object-contain" muted preload="metadata" />
                      ) : (
                        <img src={mediaUrl("nav-ads-media", a.storage_path)} alt={a.title} className="h-full w-auto object-contain" />
                      )}
                    </div>
                    <div className="p-2 space-y-1.5">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs text-slate-700 truncate">{a.title || "Sans titre"}</p>
                        <Badge className={a.published
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 shrink-0 text-[10px] px-1.5 py-0"
                          : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 shrink-0 text-[10px] px-1.5 py-0"}
                          data-testid={`navad-status-${a.id}`}>
                          {a.published ? "Publié" : "Brouillon"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm"
                          className={a.published ? "bg-slate-500 hover:bg-slate-600 flex-1 h-7 text-xs" : "bg-emerald-600 hover:bg-emerald-700 flex-1 h-7 text-xs"}
                          onClick={() => togglePublished(`/admin/nav-ads/${a.id}/toggle`, loadNavAds)} data-testid={`navad-toggle-${a.id}`}>
                          {a.published ? "Désactiver" : "Publier"}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 shrink-0"
                          onClick={() => deleteMedia(`/admin/nav-ads/${a.id}`, loadNavAds)} data-testid={`navad-delete-${a.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="retro" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="retro-admin-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Second bandeau publicitaire</h2>
              <p className="text-sm text-slate-500">Images, documents PDF et mini-clips vidéo — affichés sur l'accueil sous « Notre film » dans le bandeau « Rétrospective &amp; Pub » (défilement toutes les 2 secondes).</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="retro-title">Titre / légende</Label>
                <Input id="retro-title" value={retroForm.title} maxLength={120}
                  onChange={(e) => setRetroForm({ ...retroForm, title: e.target.value })} data-testid="retro-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="retro-link">Lien (optionnel)</Label>
                <Input id="retro-link" value={retroForm.link} maxLength={300} placeholder="https://…"
                  onChange={(e) => setRetroForm({ ...retroForm, link: e.target.value })} data-testid="retro-link-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="retro-file">Fichier (image, PDF ou vidéo, 30 Mo max)</Label>
                <Input id="retro-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4,video/webm,video/quicktime"
                  onChange={(e) => setRetroForm({ ...retroForm, file: e.target.files?.[0] || null })} data-testid="retro-file-input" />
              </div>
            </div>
            <Button className="bg-sky-700 hover:bg-sky-800" disabled={retroSaving}
              onClick={() => sendMedia("/admin/retro", retroForm, setRetroSaving, loadRetro, () => setRetroForm({ title: "", link: "", file: null }), "Élément ajouté au bandeau")}
              data-testid="retro-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {retroSaving ? "Envoi en cours…" : "Ajouter au bandeau"}
            </Button>
          </Card>

          {retro.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="retro-empty">
              Aucun élément pour l'instant — le bandeau ne s'affiche pas sur le site tant qu'il est vide.
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {retro.map((it) => (
                <Card key={it.id} className="border-slate-200 overflow-hidden" data-testid={`retro-item-${it.id}`}>
                  {it.media_type === "image" ? (
                    <img src={mediaUrl("retro-media", it.storage_path)} alt={it.title} className="w-full h-32 object-cover" />
                  ) : it.media_type === "video" ? (
                    <video src={mediaUrl("retro-media", it.storage_path)} className="w-full h-32 object-cover" muted preload="metadata" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-slate-100 text-xs font-bold uppercase tracking-widest text-slate-500">Document PDF</div>
                  )}
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate">{it.title || "Sans titre"}</p>
                      <Badge className={it.published
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 shrink-0"
                        : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 shrink-0"}
                        data-testid={`retro-status-${it.id}`}>
                        {it.published ? "Publié" : "Brouillon"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm"
                        className={it.published ? "bg-slate-500 hover:bg-slate-600 flex-1" : "bg-emerald-600 hover:bg-emerald-700 flex-1"}
                        onClick={() => togglePublished(`/admin/retro/${it.id}/toggle`, loadRetro)} data-testid={`retro-toggle-${it.id}`}>
                        {it.published ? "Désactiver" : "Publier"}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => deleteMedia(`/admin/retro/${it.id}`, loadRetro)} data-testid={`retro-delete-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="music" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="music-admin-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Musique du site</h2>
                <p className="text-sm text-slate-500">Bibliothèque de {music?.tracks?.length ?? 0}/15 morceaux — les visiteurs peuvent couper ou activer le son depuis le bouton rond en bas à gauche du site.</p>
              </div>
              <div className="flex items-center gap-2.5">
                <Label htmlFor="music-enabled" className="text-sm text-slate-700">Musique sur le site</Label>
                <Switch id="music-enabled" checked={!!music?.enabled} onCheckedChange={toggleMusicEnabled} data-testid="music-enabled-switch" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="music-title">Titre du morceau</Label>
                <Input id="music-title" value={musicForm.title} maxLength={120}
                  onChange={(e) => setMusicForm({ ...musicForm, title: e.target.value })} data-testid="music-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="music-file">Fichier audio (MP3, OGG, WAV… 30 Mo max)</Label>
                <Input id="music-file" type="file" accept="audio/*"
                  onChange={(e) => setMusicForm({ ...musicForm, file: e.target.files?.[0] || null })} data-testid="music-file-input" />
              </div>
            </div>
            <Button className="bg-sky-700 hover:bg-sky-800" disabled={musicSaving || (music?.tracks?.length ?? 0) >= 15}
              onClick={() => sendMedia("/admin/music", musicForm, setMusicSaving, loadMusic, () => setMusicForm({ title: "", file: null }), "Morceau ajouté à la bibliothèque")}
              data-testid="music-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> {musicSaving ? "Envoi en cours…" : "Ajouter le morceau"}
            </Button>
          </Card>

          {music?.tracks?.length > 0 && (
            <Card className="border-slate-200 p-4 space-y-2" data-testid="music-list">
              {music.tracks.map((t, i) => (
                <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2" data-testid={`music-track-${t.id}`}>
                  <p className="text-sm text-slate-700 truncate"><span className="text-slate-400 mr-2">{i + 1}.</span>{t.title || "Morceau sans titre"}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={t.published
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}
                      data-testid={`music-status-${t.id}`}>
                      {t.published ? "Publié" : "Brouillon"}
                    </Badge>
                    <Button size="sm"
                      className={t.published ? "bg-slate-500 hover:bg-slate-600" : "bg-emerald-600 hover:bg-emerald-700"}
                      onClick={() => togglePublished(`/admin/music/${t.id}/toggle`, loadMusic)} data-testid={`music-toggle-${t.id}`}>
                      {t.published ? "Désactiver" : "Publier"}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteMedia(`/admin/music/${t.id}`, loadMusic)} data-testid={`music-delete-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>
        <TabsContent value="season" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-5" data-testid="season-admin-card">
            {!seasonal ? (
              <p className="text-sm text-slate-500">Chargement…</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Effets saisonniers du site</h2>
                    <p className="text-sm text-slate-500">Un mode à la fois : bandeau animé en haut de l'accueil, rafale d'intro thématique et mini-clip d'ouverture selon la saison.</p>
                  </div>
                  <Badge className={seasonal.mode && seasonal.mode !== "none"
                    ? "bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100"
                    : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}
                    data-testid="seasonal-mode-badge">
                    {seasonal.mode && seasonal.mode !== "none"
                      ? `Mode ${SEASONAL_OPTIONS.find((o) => o.value === seasonal.mode)?.label || seasonal.mode} actif`
                      : "Aucun effet actif"}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Mode saisonnier</Label>
                    <Select value={seasonal.mode || "none"} onValueChange={(v) => setSeasonal({ ...seasonal, mode: v })}>
                      <SelectTrigger data-testid="seasonal-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SEASONAL_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`seasonal-mode-${o.value}`}>
                            {o.label} — {o.desc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="seasonal-text">Texte du bandeau (optionnel)</Label>
                    <Input id="seasonal-text" value={seasonal.seasonal_text} maxLength={120} placeholder="Joyeux Halloween !"
                      onChange={(e) => setSeasonal({ ...seasonal, seasonal_text: e.target.value })} data-testid="seasonal-text-input" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-200">
                  <Button className="bg-sky-700 hover:bg-sky-800" onClick={() => saveSeasonal()} disabled={seasonalSaving} data-testid="seasonal-save-btn">
                    {seasonalSaving ? "Enregistrement…" : "Enregistrer le mode"}
                  </Button>
                  {seasonal.mode && seasonal.mode !== "none" && (
                    <Button className="bg-red-600 hover:bg-red-700" onClick={() => saveSeasonal({ mode: "none" })} disabled={seasonalSaving} data-testid="seasonal-disable-btn">
                      Désactiver l'effet
                    </Button>
                  )}
                </div>
              </>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="participants" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-5" data-testid="participants-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Participants aux événements</h2>
              <p className="text-sm text-slate-500">Inscriptions « Je participe » : événement, nombre de participants, coordonnées et acceptation du règlement général et des CGV.</p>
            </div>
            {participants.length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="participants-empty">Aucune inscription pour le moment.</p>
            )}
            {Object.entries(participantsByEvent).map(([title, list]) => (
              <div key={title} className="space-y-2" data-testid={`participants-group-${title}`}>
                <div className="flex flex-wrap items-center gap-2.5 pt-2">
                  <h3 className="text-sm font-bold text-slate-800">{title}</h3>
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" data-testid={`participants-count-${title}`}>
                    {list.length} inscription{list.length > 1 ? "s" : ""}
                  </Badge>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${list[0].event_id}/participants.csv`, "_blank")}
                    data-testid={`participants-csv-${list[0].event_id}`}>Export CSV</Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${list[0].event_id}/participants.pdf`, "_blank")}
                    data-testid={`participants-pdf-${list[0].event_id}`}>Export PDF (émargement)</Button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Adulte(s)</TableHead>
                        <TableHead>Enfant(s)</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Règlement & CGV</TableHead>
                        <TableHead>Inscrit le</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.map((p) => (
                        <TableRow key={p.id} data-testid={`participant-row-${p.id}`}>
                          <TableCell className="text-sm font-medium">{p.adults}</TableCell>
                          <TableCell className="text-sm">{p.children_count > 0 ? `${p.children_count} — ${p.children_names}` : "—"}</TableCell>
                          <TableCell className="text-sm">{p.phone}<br /><span className="text-slate-500">{p.email}</span></TableCell>
                          <TableCell>
                            <Badge className={p.member_status === "adherent"
                              ? "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100"
                              : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100"}>
                              {p.member_status === "adherent" ? "Adhérent" : "Non adhérent"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {p.terms_accepted ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" data-testid={`participant-terms-${p.id}`}>Oui</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100" data-testid={`participant-terms-${p.id}`}>Non</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 whitespace-nowrap">{formatDate(p.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="idees" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-4" data-testid="ideas-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Boîte à idées publique</h2>
              <p className="text-sm text-slate-500">Idées glissées par les visiteurs et les adhérents depuis l'onglet latéral droit du site (événements, activités, améliorations…).</p>
            </div>
            {ideas.length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="ideas-empty">Aucune idée pour le moment.</p>
            )}
            {[
              { key: "nouveau", title: "Nouvelles idées", list: ideas.filter((i) => (i.status || "nouveau") === "nouveau") },
              { key: "en_cours", title: "En cours de traitement", list: ideas.filter((i) => i.status === "en_cours") },
              { key: "classee", title: "Classées", list: ideas.filter((i) => ["terminee", "a_developper", "refusee"].includes(i.status)) },
            ].map((section) => section.list.length > 0 && (
              <div key={section.key} className="space-y-2.5" data-testid={`ideas-section-${section.key}`}>
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 pt-2">{section.title} ({section.list.length})</h3>
                {section.list.map((idea) => {
                  const num = ideas.indexOf(idea) + 1;
                  const dt = new Date(idea.created_at);
                  const when = `${dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })} à ${dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
                  const st = idea.status || "nouveau";
                  return (
                    <div key={idea.id} className="rounded-xl border border-slate-200 p-3.5 flex items-start justify-between gap-3" data-testid={`idea-row-${idea.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-400" data-testid={`idea-num-${idea.id}`}>Idée n°{num} · {when}</p>
                        <p className="text-sm text-slate-800 whitespace-pre-line break-words mt-1">{idea.message}</p>
                        <p className="text-xs text-slate-400 mt-1.5">{idea.name || "Anonyme"}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        {st === "nouveau" && (
                          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid={`idea-treat-${idea.id}`}>
                            <Checkbox onCheckedChange={(v) => v === true && setIdeaStatus(idea.id, "en_cours")} /> Traiter
                          </label>
                        )}
                        {st === "en_cours" && (
                          <>
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid={`idea-finish-${idea.id}`}>
                              <Checkbox onCheckedChange={(v) => v === true && setIdeaStatus(idea.id, "terminee")} /> Terminer
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid={`idea-develop-${idea.id}`}>
                              <Checkbox onCheckedChange={(v) => v === true && setIdeaStatus(idea.id, "a_developper")} /> À développer
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid={`idea-reject-${idea.id}`}>
                              <Checkbox onCheckedChange={(v) => v === true && setIdeaStatus(idea.id, "refusee")} /> Refuser
                            </label>
                          </>
                        )}
                        {st === "terminee" && <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Terminée</Badge>}
                        {st === "a_developper" && <Badge className="bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100">À développer</Badge>}
                        {st === "refusee" && <Badge className="bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-100">Refusée</Badge>}
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7"
                          onClick={() => deleteIdea(idea.id)} data-testid={`idea-delete-${idea.id}`} aria-label="Supprimer cette idée">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="legal" className="space-y-4">
          <Card className="border-slate-200 p-5 sm:p-6 space-y-5" data-testid="legal-docs-card">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Documents contractuels</h2>
              <p className="text-sm text-slate-500">Règlement général et conditions générales de vente (PDF, 30 Mo max). Dès qu'un document est en ligne, le participant doit le télécharger puis cocher l'acceptation avant de pouvoir valider son inscription à un événement.</p>
            </div>
            {[
              { kind: "reglement", label: "Règlement général" },
              { kind: "cgv", label: "Conditions générales de vente (CGV)" },
            ].map(({ kind, label }) => (
              <div key={kind} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`legal-doc-${kind}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-800">{label}</p>
                  {legalDocs[kind] ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" data-testid={`legal-status-${kind}`}>En ligne</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100" data-testid={`legal-status-${kind}`}>Non déposé</Badge>
                  )}
                </div>
                {legalDocs[kind] && (
                  <div className="flex flex-wrap items-center gap-2.5 text-sm text-slate-600">
                    <span className="truncate max-w-[260px]" data-testid={`legal-filename-${kind}`}>{legalDocs[kind].filename}</span>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/legal-docs/${kind}/download`} target="_blank" rel="noopener noreferrer"
                      className="text-sky-700 hover:underline text-xs font-medium" data-testid={`legal-download-admin-${kind}`}>Télécharger</a>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7"
                      onClick={() => removeLegalDoc(kind)} data-testid={`legal-delete-${kind}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
                    </Button>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor={`legal-file-${kind}`}>{legalDocs[kind] ? "Remplacer le fichier" : "Déposer le fichier"}</Label>
                  <Input id={`legal-file-${kind}`} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="max-w-md"
                    data-testid={`legal-file-input-${kind}`} disabled={legalSaving === kind}
                    onChange={(e) => { uploadLegalDoc(kind, e.target.files?.[0] || null); e.target.value = ""; }} />
                  {legalSaving === kind && <p className="text-xs text-slate-400">Envoi en cours…</p>}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingEvent ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
            <DialogDescription>Événement affiché sur la page Événements du site.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEvent} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="event-form-title" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date et heure</Label>
                <DateTimeField value={eventForm.date} onChange={(v) => setEventForm({ ...eventForm, date: v })} testid="event-form-date" />
              </div>
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
                <div className="space-y-2">
                  <Label className="text-slate-700">Prévision de clôture d'événement</Label>
                  <DateTimeField value={eventForm.closes_at} onChange={(v) => setEventForm({ ...eventForm, closes_at: v })} testid="event-form-closes-at" />
                  <p className="text-xs text-slate-400">Passée cette date et cette heure, les inscriptions « Je participe » se ferment automatiquement.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">À partir de ce nombre de participations, clôture automatique</Label>
                  <Input type="number" min="0" max="5000" value={eventForm.max_participations}
                    onChange={(e) => setEventForm({ ...eventForm, max_participations: Math.max(0, parseInt(e.target.value || "0", 10) || 0) })}
                    data-testid="event-form-max-participations" className="max-w-[140px]" />
                  <p className="text-xs text-slate-400">0 = aucune limite. Quota atteint : l'événement devient « complet » et les inscriptions se ferment.</p>
                </div>
              </div>
              {eventForm.members_only && (
                <div className="space-y-3 rounded-xl border border-violet-200 bg-violet-50 p-3.5" data-testid="event-internal-auth-block">
                  <div className="space-y-2">
                    <Label className="text-violet-800">Code d'authentification interne (6 caractères)</Label>
                    <Input value={eventForm.internal_code} maxLength={6} placeholder="Ex. : A7K3P9" autoComplete="off"
                      onChange={(e) => setEventForm({ ...eventForm, internal_code: e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 6) })}
                      data-testid="event-form-internal-code" className="max-w-[200px] font-mono tracking-[0.3em] uppercase" />
                    <p className="text-xs text-violet-500">À transmettre en interne aux adhérents — exigé lors de l'inscription « Je participe ».</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-violet-800">Couleur de vérification attendue</Label>
                    <div className="flex gap-2.5">
                      {[{ id: "rouge", c: "#dc2626" }, { id: "bleu", c: "#2563eb" }, { id: "jaune", c: "#eab308" }, { id: "noir", c: "#171717" }].map((col) => (
                        <button key={col.id} type="button" onClick={() => setEventForm({ ...eventForm, required_color: col.id })}
                          data-testid={`event-form-color-${col.id}`} aria-label={`Couleur ${col.id}`}
                          className={`h-9 w-9 rounded-full border-4 transition-all ${eventForm.required_color === col.id ? "border-violet-600 scale-110 shadow-md" : "border-white shadow"}`}
                          style={{ backgroundColor: col.c }} />
                      ))}
                    </div>
                    <p className="text-xs text-violet-500">Renforce l'authentification : l'adhérent doit choisir cette couleur pour valider.</p>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Lieu</Label>
                <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} data-testid="event-form-location" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={4} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} data-testid="event-form-description" />
            </div>
            <div className="space-y-2">
              <Label>Affiche de l'événement (optionnel)</Label>
              {eventForm.image_url && (
                <img src={eventForm.image_url} alt="Affiche actuelle" className="h-24 rounded-xl object-cover border border-slate-200" data-testid="event-form-image-preview" />
              )}
              <Input type="file" accept="image/*" onChange={(e) => setEventFile(e.target.files[0] || null)} data-testid="event-form-image" />
              <p className="text-xs text-slate-400">PNG ou JPG — l'affiche s'affichera à côté de la date sur la page Événements.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <Label htmlFor="event-members-only" className="font-normal text-sm">Réservé aux adhérents connectés</Label>
              <Switch id="event-members-only" checked={eventForm.members_only}
                onCheckedChange={(v) => setEventForm({ ...eventForm, members_only: v })} data-testid="event-form-members-only" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <Label htmlFor="event-published" className="font-normal text-sm">Publié sur le site</Label>
              <Switch id="event-published" checked={eventForm.published}
                onCheckedChange={(v) => setEventForm({ ...eventForm, published: v })} data-testid="event-form-published" />
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="event-form-submit">
              {saving ? "Enregistrement…" : editingEvent ? "Enregistrer" : "Publier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={newsDialog} onOpenChange={setNewsDialog}>
        <DialogContent className="max-w-lg" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingNews ? "Modifier l'actualité" : "Nouvelle actualité"}</DialogTitle>
            <DialogDescription>Article affiché sur la page Actualités du site.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNews} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input required value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} data-testid="news-form-title" />
            </div>
            <div className="space-y-2">
              <Label>Contenu</Label>
              <Textarea rows={8} required value={newsForm.content} onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })} data-testid="news-form-content" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
              <Label htmlFor="news-published" className="font-normal text-sm">Publiée sur le site</Label>
              <Switch id="news-published" checked={newsForm.published}
                onCheckedChange={(v) => setNewsForm({ ...newsForm, published: v })} data-testid="news-form-published" />
            </div>
            <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="news-form-submit">
              {saving ? "Enregistrement…" : editingNews ? "Enregistrer" : "Publier"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
